import {
  BadgePercent,
  BarChart3,
  Boxes,
  ClipboardList,
  CreditCard,
  Globe,
  LayoutTemplate,
  Megaphone,
  Palette,
  QrCode,
  ReceiptText,
  Share2,
  ShoppingCart,
  Sparkles,
  Store,
  Truck,
  UserRound,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { WhatsAppIcon } from '@/components/WhatsAppIcon';
import { registerSiteContent } from '@/features/siteContent/runtime';

/**
 * The marketing navigation - one source of truth shared by the header mega
 * menus, the footer and the `/features/:slug` pages, so a menu item can never
 * point at a page that does not exist.
 */

export interface FeatureNavItem {
  slug: string;
  label: string;
  /** One line under the label in menus and cards. */
  tagline: string;
  icon: LucideIcon;
}

export interface ProductNavGroup {
  heading: string;
  items: FeatureNavItem[];
}

export const featurePath = (slug: string) => `/features/${slug}`;

export const PRODUCT_NAV_GROUPS: ProductNavGroup[] = [
  {
    heading: 'Build your store',
    items: [
      {
        slug: 'store-builder',
        label: 'Store builder',
        tagline: 'A polished storefront, no code needed',
        icon: LayoutTemplate,
      },
      {
        slug: 'themes',
        label: 'Themes & design',
        tagline: 'Looks designed for Indian D2C brands',
        icon: Palette,
      },
      {
        slug: 'custom-domains',
        label: 'Custom domains',
        tagline: 'Sell on your own .in or .com',
        icon: Globe,
      },
      {
        slug: 'customer-accounts',
        label: 'Customer accounts',
        tagline: 'Sign-in, order history and saved details',
        icon: UserRound,
      },
      {
        slug: 'ai-assistant',
        label: 'AI assistant',
        tagline: 'Product copy, SEO and support drafts',
        icon: Sparkles,
      },
    ],
  },
  {
    heading: 'Sell everywhere',
    items: [
      {
        slug: 'online-store',
        label: 'Online store',
        tagline: 'Your 24×7 storefront on the web',
        icon: Store,
      },
      {
        slug: 'whatsapp-commerce',
        label: 'WhatsApp Commerce',
        tagline: 'Sell where India already chats',
        icon: WhatsAppIcon,
      },
      {
        slug: 'social-selling',
        label: 'Social & marketplaces',
        tagline: 'Instagram, Facebook and beyond',
        icon: Share2,
      },
      {
        slug: 'share-links',
        label: 'Links & QR codes',
        tagline: 'A shop link for every conversation',
        icon: QrCode,
      },
    ],
  },
  {
    heading: 'Manage your business',
    items: [
      {
        slug: 'orders',
        label: 'Orders & invoices',
        tagline: 'Every order, from placed to delivered',
        icon: ClipboardList,
      },
      {
        slug: 'inventory',
        label: 'Inventory',
        tagline: 'Stock that never drifts out of sync',
        icon: Boxes,
      },
      {
        slug: 'shipping',
        label: 'Shipping & delivery',
        tagline: 'Rates, labels and live tracking',
        icon: Truck,
      },
      {
        slug: 'customers',
        label: 'Customers',
        tagline: 'Know who buys, and buys again',
        icon: Users,
      },
    ],
  },
  {
    heading: 'Market & grow',
    items: [
      {
        slug: 'discounts',
        label: 'Discounts & coupons',
        tagline: 'Codes and offers that convert',
        icon: BadgePercent,
      },
      {
        slug: 'whatsapp-campaigns',
        label: 'WhatsApp campaigns',
        tagline: 'Broadcasts on the official API',
        icon: Megaphone,
      },
      {
        slug: 'analytics',
        label: 'Analytics & insights',
        tagline: 'See what sells and why',
        icon: BarChart3,
      },
    ],
  },
  {
    heading: 'Get paid',
    items: [
      {
        slug: 'payments',
        label: 'Payments',
        tagline: 'UPI, cards, netbanking and COD',
        icon: CreditCard,
      },
      {
        slug: 'checkout',
        label: 'Checkout',
        tagline: 'Fast, mobile-first and trustworthy',
        icon: ShoppingCart,
      },
      {
        slug: 'gst-invoices',
        label: 'GST invoices',
        tagline: 'Compliant invoices on every order',
        icon: ReceiptText,
      },
    ],
  },
];

export function findFeatureNavItem(slug: string) {
  // Looked up on every call rather than flattened once: the owner's edits are
  // applied to PRODUCT_NAV_GROUPS after this module loads.
  for (const group of PRODUCT_NAV_GROUPS) {
    const item = group.items.find((candidate) => candidate.slug === slug);
    if (item) return { ...item, group: group.heading };
  }
  return undefined;
}

registerSiteContent('nav.products', { PRODUCT_NAV_GROUPS });
